import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PocketPanel,
} from "@/components/ui";
import { useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import type { FrameChromeTool } from "./frame-chrome-config";
import { useFrameChromeHidden } from "./frame-renderer-context";
import { useCanvasStore } from "@/stores/canvas-store";

interface FrameChromeToolsProps {
  tools: FrameChromeTool[];
  onOpenChange?: (open: boolean) => void;
  showLabels?: boolean;
}

/** A frame's toolbar tools; none where the surface owns the chrome. */
export function FrameChromeTools(props: FrameChromeToolsProps) {
  return useFrameChromeHidden() ? null : <FrameChromeToolsBody {...props} />;
}

function FrameChromeToolsBody({
  tools,
  onOpenChange,
  showLabels = false,
}: FrameChromeToolsProps) {
  const t = useT(framesSharedMessages);
  const [openId, setOpenId] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const focusedPopoverRef = useRef<string | null>(null);
  const updateOpen = (id: string | null, restoreFocus = false) => {
    if (!id && restoreFocus) anchorRef.current?.focus();
    focusedPopoverRef.current = null;
    setOpenId(id);
    setPopoverPosition(null);
    onOpenChange?.(id !== null);
  };
  useLayoutEffect(() => {
    if (!openId) return;
    const positionPopover = () => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;
      const anchorRect = anchor.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      const margin = 8;
      const left = Math.max(
        margin,
        Math.min(
          anchorRect.right - popoverRect.width,
          window.innerWidth - popoverRect.width - margin,
        ),
      );
      const below = anchorRect.bottom + margin;
      const top = Math.max(
        margin,
        Math.min(
          below + popoverRect.height <= window.innerHeight - margin
            ? below
            : anchorRect.top - popoverRect.height - margin,
          window.innerHeight - popoverRect.height - margin,
        ),
      );
      setPopoverPosition((current) =>
        current?.left === left && current.top === top ? current : { left, top },
      );
    };
    positionPopover();
    const unsubscribe = useCanvasStore.subscribe(positionPopover);
    window.addEventListener("resize", positionPopover);
    window.addEventListener("scroll", positionPopover, true);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(positionPopover);
    if (popoverRef.current) observer?.observe(popoverRef.current);
    return () => {
      unsubscribe();
      window.removeEventListener("resize", positionPopover);
      window.removeEventListener("scroll", positionPopover, true);
      observer?.disconnect();
    };
  }, [openId]);
  useLayoutEffect(() => {
    if (!openId || !popoverPosition || focusedPopoverRef.current === openId)
      return;
    const popover = popoverRef.current;
    if (!popover) return;
    const firstControl = popover.querySelector<HTMLElement>(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
    );
    (firstControl ?? popover).focus();
    focusedPopoverRef.current = openId;
  }, [openId, popoverPosition]);
  useEffect(() => {
    if (
      openId &&
      !tools.some((tool) => tool.id === openId && tool.kind === "popover")
    ) {
      if (popoverRef.current?.contains(document.activeElement))
        anchorRef.current?.focus();
      focusedPopoverRef.current = null;
      setOpenId(null);
      setPopoverPosition(null);
      onOpenChange?.(false);
    }
  }, [openId, tools, onOpenChange]);
  useEffect(() => {
    if (!openId) return;
    const close = (event: PointerEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent && event.key !== "Escape") return;
      if (
        event instanceof PointerEvent &&
        (root.current?.contains(event.target as Node) ||
          popoverRef.current?.contains(event.target as Node))
      )
        return;
      if (event instanceof KeyboardEvent) anchorRef.current?.focus();
      focusedPopoverRef.current = null;
      setOpenId(null);
      setPopoverPosition(null);
      onOpenChange?.(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [openId, onOpenChange]);
  return (
    <div ref={root} className="contents">
      {tools.map((tool) => {
        if (tool.kind === "label")
          return (
            <span
              key={tool.id}
              className="min-w-0 max-w-[180px] truncate px-2 text-appkit-caption text-card-muted"
              title={tool.label}
              data-testid={`frame-chrome-${tool.id}`}
            >
              {tool.label}
            </span>
          );
        if (tool.kind === "custom")
          return (
            <div
              key={tool.id}
              className="contents"
              data-testid={`frame-chrome-${tool.id}`}
            >
              {tool.render((open) => onOpenChange?.(open))}
            </div>
          );
        const Icon = tool.icon;
        const labelled = showLabels || !Icon;
        const trigger = (
          <Button
            type="button"
            variant="ghost"
            size={labelled ? "sm" : "iconSm"}
            className={labelled ? "nodrag gap-1.5 px-2.5" : undefined}
            aria-label={
              (tool.kind === "button" && tool.ariaLabel) || tool.label
            }
            title={(tool.kind === "button" && tool.ariaLabel) || tool.label}
            data-testid={`frame-chrome-${tool.id}`}
            disabled={tool.kind === "button" ? tool.disabled : undefined}
            aria-pressed={tool.kind === "button" ? tool.pressed : undefined}
            aria-expanded={
              tool.kind === "popover" ? openId === tool.id : undefined
            }
            aria-haspopup={tool.kind === "popover" ? "dialog" : undefined}
            onClick={
              tool.kind === "button"
                ? tool.onSelect
                : tool.kind === "popover"
                  ? (event) => {
                      anchorRef.current = event.currentTarget;
                      updateOpen(openId === tool.id ? null : tool.id);
                    }
                  : undefined
            }
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {labelled && <span className="min-w-0 truncate">{tool.label}</span>}
          </Button>
        );
        if (tool.kind === "menu")
          return (
            <DropdownMenu key={tool.id} onOpenChange={onOpenChange}>
              <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                data-testid={`frame-chrome-${tool.id}-menu`}
              >
                {tool.items.map((item) => (
                  <DropdownMenuItem
                    key={item.id}
                    onSelect={item.onSelect}
                    destructive={item.destructive}
                    icon={
                      item.icon ? <item.icon className="h-4 w-4" /> : undefined
                    }
                    data-testid={`frame-chrome-${tool.id}-${item.id}`}
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        return (
          <div key={tool.id} className="contents">
            {trigger}
            {tool.kind === "popover" &&
              openId === tool.id &&
              createPortal(
                <PocketPanel
                  ref={popoverRef}
                  nativeGroup={`frame-tool-${tool.id}`}
                  nativeSurface={false}
                  testId={`frame-chrome-${tool.id}-popover`}
                  role="dialog"
                  aria-label={t("toolOptions", { tool: tool.label })}
                  tabIndex={-1}
                  className="fixed z-[31000] w-max max-w-[90vw] overflow-y-auto"
                  style={{
                    left: popoverPosition?.left ?? 0,
                    top: popoverPosition?.top ?? 0,
                    maxHeight: "calc(100vh - 16px)",
                    visibility: popoverPosition ? "visible" : "hidden",
                  }}
                  onPointerDown={(event) => event.stopPropagation()}
                  onWheel={(event) => event.stopPropagation()}
                >
                  {tool.renderPopover(() => updateOpen(null, true))}
                </PocketPanel>,
                document.body,
              )}
          </div>
        );
      })}
    </div>
  );
}
